import React from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useLocation } from "react-router-dom";

import { useNavigateWithClient } from "hooks";
import { Session } from "utils";
import { useStackSnackbar } from "contexts/StackSnackbarProvider";
import useAssessmentStore from "components/Assessment/assessmentStore";
import { AssessmentStates } from "components/Assessment/constants";

/**
 * Post-assessment wait screen (mobile) — mobile equivalent of
 * `Assessment/PostAssessment`.
 *
 * Shown while answers finish uploading/analysing (WAITING_FOR_RESULT →
 * FINISHING). On COMPLETED it resets the store and hands off to the attempt's
 * feedback page — or back out to the magic-link URL, exactly as desktop does.
 */
export default function PostAssessment({ assessmentState }) {
  const navigate = useNavigateWithClient();
  const location = useLocation();
  const { closeStackSnackbar } = useStackSnackbar();

  const attempt = useAssessmentStore((s) => s.attempt);
  const reset = useAssessmentStore((s) => s.reset);

  React.useEffect(() => {
    closeStackSnackbar();

    if (assessmentState === AssessmentStates.COMPLETED) {
      const attemptId = attempt._id;

      if (Session.magicLinkRedirectUrl) {
        window.open(Session.magicLinkRedirectUrl, "_top");
      } else {
        reset();
        navigate(`/performance/${attemptId}`, {
          state: { from: location.pathname },
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentState, attempt]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        gap: 5,
        px: 6,
        bgcolor: "shades.clr-offWhite",
      }}
    >
      <CircularProgress />
      <Typography variant="body01-medium" color="primary.clr-300">
        Please wait...
        <br />
        Our AI is analysing your answer.
        <br />
        Do not close the tab or browser window.
      </Typography>
    </Box>
  );
}
